import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  Home, FileText, Users, Swords, User, LogOut, Search, X, UserPlus, 
  UserCheck, Loader2, CheckCircle, Bell, UserPlus2, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';

const APP_NAME = process.env.REACT_APP_APP_NAME || 'POLIVOX';
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Badge icons mapping - using image paths
const BADGE_ICONS = {
  indian_flag: "/badges/indian_flag.png",
  bjp: "/badges/bjp.png",
  inc: "/badges/inc.png",
  aap: "/badges/aap.png",
  tmc: "/badges/tmc.png",
  dmk: "/badges/dmk.png",
  sp: "/badges/sp.png",
  bsp: "/badges/bsp.png",
  ncp: "/badges/ncp.png",
  shiv_sena: "/badges/shiv_sena.png",
  jdu: "/badges/jdu.png",
  rjd: "/badges/rjd.png",
  ysrcp: "/badges/ysrcp.png",
  tdp: "/badges/tdp.png",
  brs: "/badges/brs.png",
  cpim: "/badges/cpim.png",
  polivox_citizen: "/badges/polivox_citizen.png",
  civic_activist: "/badges/civic_activist.png",
  tree: "/badges/tree.png",
  mountain: "/badges/mountain.png",
  river: "/badges/river.png"
};

const DEFAULT_BADGE = "/badges/polivox_citizen.png";

const Navigation = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [friendStatus, setFriendStatus] = useState({});
  const [followStatus, setFollowStatus] = useState({});
  const [actionLoading, setActionLoading] = useState({});
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const searchRef = useRef(null);
  const mobileSearchRef = useRef(null);
  const mobileSearchInputRef = useRef(null);
  const searchTimeout = useRef(null);
  
  // Notification state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const notificationRef = useRef(null);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/issues', label: 'Issues', icon: FileText },
    { path: '/leaders', label: 'Leaders', icon: Users },
    { path: '/rooms/create', label: 'Room', icon: Swords },
  ];

  if (user?.is_admin) {
    navItems.push({ path: '/admin', label: 'Admin', icon: User });
  }

  const isActive = (path) => location.pathname === path;

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    
    try {
      const res = await axios.get(`${API_URL}/api/notifications`, {
        params: { limit: 20 },
        withCredentials: true
      });
      console.log('[Notifications] Fetched:', res.data);
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (error) {
      console.error('[Notifications] Error:', error.response?.data || error.message);
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [user]);

  // Fetch notifications on mount and every 30 seconds
  useEffect(() => {
    if (user) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user, fetchNotifications]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowResults(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
      if (mobileSearchRef.current && !mobileSearchRef.current.contains(e.target)) {
        setMobileSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle Escape key to close mobile search
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && mobileSearchOpen) {
        setMobileSearchOpen(false);
        setSearchQuery('');
        setSearchResults([]);
        setShowResults(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [mobileSearchOpen]);

  // Auto-focus mobile search input when opened
  useEffect(() => {
    if (mobileSearchOpen && mobileSearchInputRef.current) {
      setTimeout(() => mobileSearchInputRef.current?.focus(), 100);
    }
  }, [mobileSearchOpen]);

  // Close mobile search when navigating
  useEffect(() => {
    setMobileSearchOpen(false);
  }, [location.pathname]);

  // Debounced search
  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    
    if (searchQuery.length >= 2) {
      searchTimeout.current = setTimeout(() => {
        performSearch();
      }, 300);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
    
    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
  }, [searchQuery]);

  const performSearch = async () => {
    if (!searchQuery.trim() || searchQuery.length < 2) return;
    
    setIsSearching(true);
    console.log('[Search] Performing search for:', searchQuery, 'User:', user?.user_id);
    try {
      const res = await axios.get(`${API_URL}/api/users/search`, {
        params: { q: searchQuery, limit: 8 },
        withCredentials: true
      });
      
      console.log('[Search] Results:', res.data);
      const users = res.data.users || [];
      setSearchResults(users);
      setShowResults(true);
      
      if (users.length > 0) {
        checkRelationships(users);
      }
    } catch (error) {
      console.error('[Search] Error:', error.response?.data || error.message);
      // If not authenticated, show toast
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Please login to search users');
      }
    } finally {
      setIsSearching(false);
    }
  };

  const checkRelationships = async (users) => {
    try {
      const friendsRes = await axios.get(`${API_URL}/api/friends/requests`, { withCredentials: true });
      const received = friendsRes.data.received || [];
      const sent = friendsRes.data.sent || [];
      
      const myFriendsRes = await axios.get(`${API_URL}/api/friends/${user.user_id}`, { withCredentials: true });
      const myFriends = myFriendsRes.data.friends || [];
      
      const followingRes = await axios.get(`${API_URL}/api/following/${user.user_id}`);
      const following = followingRes.data.following || [];
      
      const newFriendStatus = {};
      const newFollowStatus = {};
      
      users.forEach(u => {
        if (myFriends.some(f => f.user_id === u.user_id)) {
          newFriendStatus[u.user_id] = 'friends';
        } else if (sent.some(r => r.receiver_user_id === u.user_id)) {
          newFriendStatus[u.user_id] = 'pending';
        } else if (received.some(r => r.sender_user_id === u.user_id)) {
          newFriendStatus[u.user_id] = 'received';
        } else {
          newFriendStatus[u.user_id] = 'none';
        }
        
        newFollowStatus[u.user_id] = following.some(f => f.user_id === u.user_id);
      });
      
      setFriendStatus(newFriendStatus);
      setFollowStatus(newFollowStatus);
    } catch (error) {
      console.error('Error checking relationships:', error);
    }
  };

  const handleSendFriendRequest = async (userId, e) => {
    e.stopPropagation();
    setActionLoading(prev => ({ ...prev, [userId]: 'friend' }));
    
    try {
      console.log('[Search] Sending friend request to:', userId);
      const res = await axios.post(`${API_URL}/api/friends/request`, 
        { receiver_user_id: userId }, 
        { withCredentials: true }
      );
      console.log('[Search] Friend request response:', res.data);
      setFriendStatus(prev => ({ ...prev, [userId]: 'pending' }));
      toast.success('Friend request sent!');
      
      // Refresh notifications to show updated unread count
      fetchNotifications();
    } catch (error) {
      console.error('[Search] Friend request error:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Failed to send request');
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: null }));
    }
  };

  const handleFollow = async (userId, e) => {
    e.stopPropagation();
    setActionLoading(prev => ({ ...prev, [userId]: 'follow' }));
    
    try {
      if (followStatus[userId]) {
        await axios.delete(`${API_URL}/api/follow/${userId}`, { withCredentials: true });
        setFollowStatus(prev => ({ ...prev, [userId]: false }));
        toast.success('Unfollowed');
      } else {
        await axios.post(`${API_URL}/api/follow/${userId}`, {}, { withCredentials: true });
        setFollowStatus(prev => ({ ...prev, [userId]: true }));
        toast.success('Following');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: null }));
    }
  };

  const handleUserClick = (userId) => {
    setShowResults(false);
    setSearchQuery('');
    navigate(`/profile/${userId}`);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      performSearch();
    } else if (e.key === 'Escape') {
      setShowResults(false);
      setSearchQuery('');
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
  };

  // Notification handlers
  const handleNotificationClick = async (notification) => {
    // Close dropdown and navigate
    setShowNotifications(false);
    const link = notification.data?.link;
    if (link) {
      navigate(link);
    } else if (notification.type === 'friend_request') {
      navigate(`/profile/${user.user_id}`);
    } else if (notification.type === 'room_joined') {
      navigate(`/rooms/${notification.data?.room_id}`);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.post(`${API_URL}/api/notifications/read-all`, {}, {
        withCredentials: true
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark notifications as read');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'friend_request':
        return <UserPlus2 className="h-5 w-5 text-purple-500" />;
      case 'friend_accepted':
        return <UserCheck className="h-5 w-5 text-green-500" />;
      case 'room_joined':
        return <Swords className="h-5 w-5 text-orange-500" />;
      default:
        return <Bell className="h-5 w-5 text-blue-500" />;
    }
  };

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <nav className="sticky top-0 z-50 w-full border-b bg-white/80 backdrop-blur-md supports-[backdrop-filter]:bg-white/60">
      <div className="max-w-7xl mx-auto px-1 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14 sm:h-16">
          {/* Logo - Always visible with text */}
          <Link 
            to="/" 
            className="flex items-center flex-shrink-0 -ml-6"
            onClick={(e) => {
              // Dispatch custom event to refresh home feed when clicking Home
              if (location.pathname === '/') {
                e.preventDefault();
                window.dispatchEvent(new CustomEvent('refreshHomeFeed'));
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
          >
            <img src="/logo.png" alt={APP_NAME} className="h-[62px] sm:h-[79px] w-auto" />
            <span className="text-lg sm:text-2xl font-bold text-purple-800 tracking-tight -ml-4 flex items-center">{APP_NAME}</span>
          </Link>

          {/* Desktop Search Bar - Hidden on mobile */}
          {user && (
            <div className="hidden md:block flex-1 max-w-md mx-4 relative" ref={searchRef}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onFocus={() => searchResults.length > 0 && setShowResults(true)}
                  placeholder="Search users..."
                  className="w-full pl-10 pr-10 py-2 text-sm border border-gray-200 rounded-full bg-gray-50 focus:bg-white focus:border-purple-300 focus:ring-2 focus:ring-purple-100 outline-none transition-all"
                  data-testid="user-search-input"
                />
                {isSearching && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
                )}
                {searchQuery && !isSearching && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-200 rounded-full"
                  >
                    <X className="h-4 w-4 text-gray-400" />
                  </button>
                )}
              </div>
              
              {/* Search Results Dropdown */}
              {showResults && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50 max-h-96 overflow-y-auto" data-testid="search-results-dropdown">
                  {searchResults.length > 0 ? (
                    <div className="py-2">
                      {searchResults.map((u) => (
                        <div
                          key={u.user_id}
                          onClick={() => handleUserClick(u.user_id)}
                          className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                          data-testid={`search-result-${u.user_id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="relative flex-shrink-0">
                              <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200">
                                {u.picture ? (
                                  <img src={u.picture} alt={u.name} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-400 to-blue-500 text-white font-bold">
                                    {u.name?.charAt(0) || 'U'}
                                  </div>
                                )}
                              </div>
                              {u.badge_type && BADGE_ICONS[u.badge_type] && (
                                <img 
                                  src={BADGE_ICONS[u.badge_type]} 
                                  alt={u.badge_type}
                                  className="absolute -bottom-1 -right-1 w-5 h-5 object-contain rounded-full bg-white shadow-sm"
                                  onError={(e) => { e.target.src = DEFAULT_BADGE; }}
                                />
                              )}
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1">
                                <p className={`font-medium text-gray-900 truncate ${u.is_verified ? 'text-blue-800' : ''}`}>
                                  {u.name}
                                </p>
                                {u.is_verified && (
                                  <CheckCircle className="h-3.5 w-3.5 text-blue-500 fill-blue-500 flex-shrink-0" />
                                )}
                              </div>
                              {u.username && (
                                <p className="text-xs text-gray-500 truncate">@{u.username}</p>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {friendStatus[u.user_id] === 'none' && (
                                <button
                                  onClick={(e) => handleSendFriendRequest(u.user_id, e)}
                                  disabled={actionLoading[u.user_id] === 'friend'}
                                  className="p-1.5 rounded-full bg-purple-100 text-purple-600 hover:bg-purple-200 transition-colors disabled:opacity-50"
                                  title="Add Friend"
                                  data-testid={`add-friend-${u.user_id}`}
                                >
                                  {actionLoading[u.user_id] === 'friend' ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <UserPlus className="h-4 w-4" />
                                  )}
                                </button>
                              )}
                              {friendStatus[u.user_id] === 'pending' && (
                                <span className="text-xs text-gray-500 px-2">Pending</span>
                              )}
                              {friendStatus[u.user_id] === 'friends' && (
                                <span className="p-1.5 rounded-full bg-green-100 text-green-600" title="Friends">
                                  <UserCheck className="h-4 w-4" />
                                </span>
                              )}
                              
                              <button
                                onClick={(e) => handleFollow(u.user_id, e)}
                                disabled={actionLoading[u.user_id] === 'follow'}
                                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors disabled:opacity-50 ${
                                  followStatus[u.user_id]
                                    ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    : 'bg-blue-500 text-white hover:bg-blue-600'
                                }`}
                                data-testid={`follow-${u.user_id}`}
                              >
                                {actionLoading[u.user_id] === 'follow' ? (
                                  <Loader2 className="h-3 w-3 animate-spin inline" />
                                ) : followStatus[u.user_id] ? (
                                  'Following'
                                ) : (
                                  'Follow'
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="px-4 py-8 text-center text-gray-500">
                      <Search className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                      <p className="text-sm">No users found</p>
                      <p className="text-xs text-gray-400 mt-1">Try searching by name or username</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6 flex-shrink-0">
            {navItems.map((item) => {
              const Icon = item.icon;
              // Custom colors for specific tabs
              const getTabColor = () => {
                if (!isActive(item.path)) return 'text-gray-700';
                if (item.label === 'Issues') return 'text-pink-600'; // Deep pink for Issues
                if (item.label === 'Room') return 'text-purple-600'; // Purple for Rooms
                return 'text-blue-600'; // Default blue for others
              };
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`text-sm font-medium transition-colors hover:text-blue-600 flex items-center gap-1.5 ${getTabColor()}`}
                  onClick={(e) => {
                    // Refresh home feed when clicking Home while already on Home
                    if (item.path === '/' && location.pathname === '/') {
                      e.preventDefault();
                      window.dispatchEvent(new CustomEvent('refreshHomeFeed'));
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </Link>
              );
            })}
          </div>

          {/* Notification Bell & User Menu */}
          {user && (
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              {/* Mobile Search Icon - Only visible on mobile */}
              <button
                onClick={() => setMobileSearchOpen(true)}
                className="md:hidden p-2 rounded-full hover:bg-gray-100 transition-colors"
                data-testid="mobile-search-btn"
              >
                <Search className="h-5 w-5 text-gray-600" />
              </button>
              
              {/* Notification Bell */}
              <div className="relative" ref={notificationRef}>
                <button
                  onClick={async () => {
                    const wasOpen = showNotifications;
                    setShowNotifications(!showNotifications);
                    if (!wasOpen) {
                      await fetchNotifications();
                      // Auto-mark all as read when opening dropdown
                      if (unreadCount > 0) {
                        try {
                          await axios.post(`${API_URL}/api/notifications/read-all`, {}, { withCredentials: true });
                          setNotifications(prev => prev.map(n => ({ ...n, read: true })));
                          setUnreadCount(0);
                        } catch (e) {
                          console.error('Failed to mark notifications as read');
                        }
                      }
                    }
                  }}
                  className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
                  data-testid="notification-bell"
                >
                  <Bell className="h-5 w-5 text-gray-600" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1" data-testid="notification-badge">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>
                
                {/* Notification Dropdown */}
                {showNotifications && (
                  <div className="absolute top-full right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50" data-testid="notification-dropdown">
                    <div className="px-4 py-3 border-b flex items-center justify-between bg-gray-50">
                      <h3 className="font-semibold text-gray-900">Notifications</h3>
                    </div>
                    
                    <div className="max-h-96 overflow-y-auto">
                      {loadingNotifications ? (
                        <div className="flex justify-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                        </div>
                      ) : notifications.length > 0 ? (
                        notifications.map((notification) => (
                          <div
                            key={notification.notification_id}
                            onClick={() => handleNotificationClick(notification)}
                            className={`px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors border-b last:border-b-0 ${
                              !notification.read ? 'bg-blue-50/50' : ''
                            }`}
                            data-testid={`notification-${notification.notification_id}`}
                          >
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0 mt-0.5">
                                {getNotificationIcon(notification.type)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm ${!notification.read ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                                  {notification.message}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {formatTimeAgo(notification.created_at)}
                                </p>
                              </div>
                              {!notification.read && (
                                <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2" />
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-8 text-center text-gray-500">
                          <Bell className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                          <p className="text-sm">No notifications yet</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger data-testid="user-menu-trigger" className="focus:outline-none">
                  <Avatar className="h-9 w-9 cursor-pointer">
                    <AvatarImage src={user.picture} alt={user.name} />
                    <AvatarFallback>{user.name?.charAt(0) || 'U'}</AvatarFallback>
                  </Avatar>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-2 border-b">
                    <p className="text-sm font-medium">{user.name}</p>
                    <p className="text-xs text-gray-500">{user.email}</p>
                    {user.is_admin && (
                      <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                        Admin
                      </span>
                    )}
                  </div>
                  <DropdownMenuItem
                    data-testid="profile-menu-item"
                    onClick={() => navigate(`/profile/${user.user_id}`)}
                  >
                    <User className="mr-2 h-4 w-4" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="logout-menu-item" onClick={handleLogout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>

        {/* Mobile Navigation */}
        <div className="md:hidden flex overflow-x-auto gap-4 pb-3 -mx-4 px-4">
          {navItems.map((item) => {
            const Icon = item.icon;
            // Custom colors for specific tabs
            const getTabColor = () => {
              if (!isActive(item.path)) return 'text-gray-700';
              if (item.label === 'Issues') return 'text-pink-600'; // Deep pink for Issues
              if (item.label === 'Room') return 'text-purple-600'; // Purple for Rooms
              return 'text-blue-600'; // Default blue for others
            };
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`text-xs font-medium whitespace-nowrap transition-colors hover:text-blue-600 flex items-center gap-1.5 ${getTabColor()}`}
                onClick={(e) => {
                  // Refresh home feed when clicking Home while already on Home
                  if (item.path === '/' && location.pathname === '/') {
                    e.preventDefault();
                    window.dispatchEvent(new CustomEvent('refreshHomeFeed'));
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </Link>
            );
          })}
        </div>
      </div>
      {/* Mobile Search Overlay */}
      {mobileSearchOpen && user && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 md:hidden"
          onClick={() => setMobileSearchOpen(false)}
        >
          <div 
            ref={mobileSearchRef}
            className="bg-white w-full shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3">
              {/* Search Header */}
              <div className="flex items-center gap-3 mb-3">
                <button
                  onClick={() => {
                    setMobileSearchOpen(false);
                    setSearchQuery('');
                    setSearchResults([]);
                    setShowResults(false);
                  }}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                  data-testid="mobile-search-close"
                >
                  <X className="h-5 w-5 text-gray-600" />
                </button>
                <span className="text-lg font-semibold text-gray-900">Search</span>
              </div>
              
              {/* Search Input */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  ref={mobileSearchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search users..."
                  className="w-full pl-10 pr-10 py-3 text-base border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-purple-300 focus:ring-2 focus:ring-purple-100 outline-none transition-all"
                  data-testid="mobile-search-input"
                />
                {isSearching && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
                )}
                {searchQuery && !isSearching && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded-full"
                  >
                    <X className="h-4 w-4 text-gray-400" />
                  </button>
                )}
              </div>
            </div>
            
            {/* Mobile Search Results */}
            {searchResults.length > 0 && (
              <div className="max-h-[60vh] overflow-y-auto border-t border-gray-100">
                {searchResults.map((u) => (
                  <div
                    key={u.user_id}
                    onClick={() => {
                      handleUserClick(u.user_id);
                      setMobileSearchOpen(false);
                    }}
                    className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors border-b border-gray-50"
                  >
                    <div className="flex items-center gap-3">
                      <div className="relative flex-shrink-0">
                        <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200">
                          {u.picture ? (
                            <img src={u.picture} alt={u.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-400 to-blue-500 text-white font-bold text-lg">
                              {u.name?.charAt(0) || 'U'}
                            </div>
                          )}
                        </div>
                        {u.badge_type && BADGE_ICONS[u.badge_type] && (
                          <img 
                            src={BADGE_ICONS[u.badge_type]} 
                            alt={u.badge_type}
                            className="absolute -bottom-1 -right-1 w-5 h-5 object-contain rounded-full bg-white shadow-sm"
                            onError={(e) => { e.target.src = DEFAULT_BADGE; }}
                          />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{u.name}</p>
                        {u.username && (
                          <p className="text-sm text-gray-500 truncate">@{u.username}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* No Results */}
            {searchQuery && !isSearching && searchResults.length === 0 && (
              <div className="px-4 py-8 text-center text-gray-500 border-t border-gray-100">
                <Search className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                <p className="text-sm">No users found</p>
                <p className="text-xs text-gray-400 mt-1">Try searching by name or username</p>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
